import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Users, TrendingUp, CheckCircle, BarChart3,
  AlertTriangle, ArrowRight, RefreshCw, ShieldCheck,
  Activity, FileText, ClipboardList, MessageSquare, Zap,
  ChevronRight, Circle
} from 'lucide-react';
import { partnerApi, PortfolioStats, PortfolioFramework, ComplianceRow, PortfolioCompany, Partner } from '../api/partner.api';
import PageInfoButton from '@/components/PageInfoButton';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtDateShort(d: string) {
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function completionColor(pct: number) {
  if (pct >= 75) return 'text-emerald-600';
  if (pct >= 40) return 'text-amber-600';
  return 'text-red-500';
}

function completionBg(pct: number) {
  if (pct >= 75) return 'bg-emerald-500';
  if (pct >= 40) return 'bg-amber-400';
  return 'bg-red-400';
}

function statusBadge(status: string) {
  const map: Record<string, string> = {
    completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    active: 'bg-blue-100 text-blue-700 border-blue-200',
    pending: 'bg-amber-100 text-amber-700 border-amber-200',
    archived: 'bg-slate-100 text-slate-500 border-slate-200',
  };
  return map[status] || 'bg-slate-100 text-slate-600 border-slate-200';
}

function actionColor(action: string) {
  if (action?.includes('create') || action?.includes('add')) return 'bg-emerald-100 text-emerald-700';
  if (action?.includes('delete') || action?.includes('remove')) return 'bg-red-100 text-red-600';
  if (action?.includes('update') || action?.includes('edit')) return 'bg-blue-100 text-blue-700';
  if (action?.includes('login') || action?.includes('auth')) return 'bg-violet-100 text-violet-700';
  return 'bg-slate-100 text-slate-600';
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, sub, accent }: {
  icon: React.ElementType; label: string; value: string | number; sub?: string; accent: string;
}) {
  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{label}</p>
          <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
        </div>
        <div className={`p-2.5 rounded-xl ${accent}`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
    </div>
  );
}

function ProgressBar({ value, className = '' }: { value: number; className?: string }) {
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${completionBg(value)}`}
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </div>
      <span className={`text-xs font-semibold w-8 text-right tabular-nums ${completionColor(value)}`}>
        {Math.round(value)}%
      </span>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

interface FrameworkSummary {
  id: number;
  name: string;
  code: string;
  company_count: number;
  avg_completion: number;
  completed: number;
  active: number;
  pending: number;
}

interface AuditLog {
  id: number;
  actor_name: string;
  action: string;
  entity_type: string;
  entity_id: number;
  created_at: string;
}

export default function PartnerDashboard() {
  const navigate = useNavigate();

  const [partner, setPartner] = useState<Partner | null>(null);
  const [stats, setStats] = useState<PortfolioStats | null>(null);
  const [frameworks, setFrameworks] = useState<PortfolioFramework[]>([]);
  const [compliance, setCompliance] = useState<ComplianceRow[]>([]);
  const [companies, setCompanies] = useState<PortfolioCompany[]>([]);
  const [recentLogs, setRecentLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    setError(null);
    try {
      const [meRes, statsRes, fwRes, compRes, companiesRes, logsRes] = await Promise.all([
        partnerApi.getMe().catch(() => ({ partner: null })),
        partnerApi.getStats(),
        partnerApi.getFrameworks(),
        partnerApi.getCompliance(),
        partnerApi.getCompanies(),
        fetch('/api/partner/audit-logs?limit=8', { credentials: 'include' })
          .then(r => r.ok ? r.json() : { logs: [] })
          .catch(() => ({ logs: [] })),
      ]);
      setPartner((meRes as any).partner);
      setStats(statsRes);
      setFrameworks(fwRes.frameworks);
      setCompliance(compRes.data);
      setCompanies(companiesRes.companies);
      setRecentLogs(logsRes.logs || []);
      setLastUpdated(new Date());
    } catch (e: any) {
      setError(e.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }

  // Build per-framework summary from compliance rows
  const frameworkSummaries = useMemo((): FrameworkSummary[] => {
    const map = new Map<number, FrameworkSummary>();
    for (const row of compliance) {
      if (!map.has(row.framework_id)) {
        map.set(row.framework_id, {
          id: row.framework_id,
          name: row.framework_name,
          code: row.framework_code,
          company_count: 0,
          avg_completion: 0,
          completed: 0,
          active: 0,
          pending: 0,
        });
      }
      const s = map.get(row.framework_id)!;
      s.company_count++;
      s.avg_completion += row.completion_percentage;
      if (row.assignment_status === 'completed') s.completed++;
      else if (row.assignment_status === 'active') s.active++;
      else s.pending++;
    }
    for (const s of map.values()) {
      if (s.company_count > 0) s.avg_completion = s.avg_completion / s.company_count;
    }
    return Array.from(map.values()).sort((a, b) => b.company_count - a.company_count);
  }, [compliance]);

  // Companies at risk (< 40%) sorted lowest first
  const atRiskCompanies = useMemo(() =>
    companies
      .filter(c => c.is_active && c.framework_count > 0 && c.avg_completion < 40)
      .sort((a, b) => a.avg_completion - b.avg_completion)
      .slice(0, 5),
    [companies]
  );

  // Top active companies by completion
  const topCompanies = useMemo(() =>
    [...companies]
      .filter(c => c.is_active)
      .sort((a, b) => b.avg_completion - a.avg_completion)
      .slice(0, 6),
    [companies]
  );

  // ─── Loading / Error ────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-72 gap-3">
        <RefreshCw className="w-6 h-6 text-blue-500 animate-spin" />
        <p className="text-sm text-slate-500">Loading portfolio data…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="m-6 flex items-start gap-3 p-5 bg-red-50 border border-red-100 rounded-xl text-red-700">
        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium">Failed to load dashboard</p>
          <p className="text-sm mt-1 text-red-600">{error}</p>
          <button onClick={loadAll} className="mt-3 text-sm underline hover:no-underline">Try again</button>
        </div>
      </div>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-6 max-w-screen-xl mx-auto">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-5 h-5 text-blue-600" />
            <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Partner Portal</span>
          </div>
          <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">
            {partner?.name ?? 'Portfolio Dashboard'}
          </h1><PageInfoButton /></div>
          <p className="text-sm text-slate-500 mt-0.5">
            Compliance overview across {stats?.total_companies ?? 0} client {stats?.total_companies === 1 ? 'company' : 'companies'}
            {lastUpdated && (
              <span className="ml-2 text-slate-400">
                · Updated {lastUpdated.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadAll}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
          <button
            onClick={() => navigate('/partner/companies')}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Building2 className="w-3.5 h-3.5" />
            Manage Companies
          </button>
        </div>
      </div>

      {/* ── Stats Row ──────────────────────────────────────────────────── */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          <StatCard icon={Building2}   label="Total Companies"  value={stats.total_companies}                    accent="bg-blue-500" />
          <StatCard icon={CheckCircle} label="Active"           value={stats.active_companies}                   accent="bg-emerald-500"
            sub={`${stats.total_companies - stats.active_companies} inactive`} />
          <StatCard icon={Users}       label="Users"            value={stats.total_users}                        accent="bg-violet-500"
            sub="across all companies" />
          <StatCard icon={BarChart3}   label="Standards"        value={stats.total_frameworks}                   accent="bg-amber-500"
            sub="in use" />
          <StatCard icon={TrendingUp}  label="Avg Completion"   value={`${Math.round(stats.avg_completion)}%`}   accent="bg-cyan-500" />
          <StatCard icon={Zap}         label="On Track"         value={stats.companies_on_track}                 accent="bg-teal-500"
            sub="≥ 75% complete" />
        </div>
      )}

      {/* ── Middle Row ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

        {/* Framework Adoption — 2/3 width */}
        <div className="xl:col-span-2 bg-white rounded-3xl border border-slate-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Standard Adoption</h2>
              <p className="text-xs text-slate-500 mt-0.5">Compliance progress per standard across the portfolio</p>
            </div>
            <span className="text-xs text-slate-400">{frameworkSummaries.length} standard{frameworkSummaries.length !== 1 ? 's' : ''}</span>
          </div>
          {frameworkSummaries.length === 0 ? (
            <div className="py-14 text-center">
              <BarChart3 className="w-8 h-8 mx-auto text-slate-200 mb-3" />
              <p className="text-sm text-slate-400">No frameworks assigned yet</p>
              <p className="text-xs text-slate-300 mt-1">Assign compliance standards to your client companies to see progress here</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {frameworkSummaries.map(fw => (
                <div key={fw.id} className="px-5 py-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center justify-between mb-2.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded shrink-0">
                        {fw.code || fw.name.substring(0, 6).toUpperCase()}
                      </span>
                      <span className="text-sm font-medium text-slate-800 truncate">{fw.name}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 ml-4">
                      <div className="flex items-center gap-1 text-xs text-slate-400">
                        <Building2 className="w-3 h-3" />
                        {fw.company_count}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs">
                        {fw.completed > 0 && (
                          <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                            {fw.completed} done
                          </span>
                        )}
                        {fw.active > 0 && (
                          <span className="px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
                            {fw.active} active
                          </span>
                        )}
                        {fw.pending > 0 && (
                          <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                            {fw.pending} pending
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <ProgressBar value={fw.avg_completion} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* At-Risk Companies — 1/3 width */}
        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <h2 className="text-sm font-semibold text-slate-900">Needs Attention</h2>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">Companies with completion below 40%</p>
          </div>
          {atRiskCompanies.length === 0 ? (
            <div className="py-12 text-center px-5">
              <CheckCircle className="w-8 h-8 mx-auto text-emerald-300 mb-3" />
              <p className="text-sm font-medium text-slate-700">All on track</p>
              <p className="text-xs text-slate-400 mt-1">No companies are below 40% completion</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {atRiskCompanies.map(c => (
                <div
                  key={c.id}
                  className="px-5 py-3.5 hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/partner/companies/${c.id}`)}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-slate-800 truncate">{c.name}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                  </div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-xs text-slate-400">{c.framework_count} standard{c.framework_count !== 1 ? 's' : ''}</span>
                    {c.industry && <><span className="text-xs text-slate-300">·</span><span className="text-xs text-slate-400">{c.industry}</span></>}
                  </div>
                  <ProgressBar value={c.avg_completion} />
                </div>
              ))}
            </div>
          )}
          {atRiskCompanies.length > 0 && (
            <div className="px-5 py-3 border-t border-slate-50">
              <button
                onClick={() => navigate('/partner/companies')}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
              >
                View all companies <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Bottom Row ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

        {/* Company Progress List — 2/3 width */}
        <div className="xl:col-span-2 bg-white rounded-3xl border border-slate-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Company Progress</h2>
              <p className="text-xs text-slate-500 mt-0.5">Active client companies ranked by compliance completion</p>
            </div>
            <button
              onClick={() => navigate('/partner/companies')}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
            >
              All companies <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          {topCompanies.length === 0 ? (
            <div className="py-14 text-center">
              <Building2 className="w-8 h-8 mx-auto text-slate-200 mb-3" />
              <p className="text-sm text-slate-400">No companies yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Company</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden md:table-cell">Industry</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Standards</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Progress</th>
                    <th className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {topCompanies.map(c => (
                    <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3 text-left">
                        <div className="font-medium text-slate-900">{c.name}</div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <Circle className={`w-1.5 h-1.5 fill-current ${c.is_active ? 'text-emerald-500' : 'text-slate-300'}`} />
                          <span className="text-xs text-slate-400">{c.user_count} user{c.user_count !== 1 ? 's' : ''}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-left hidden md:table-cell">
                        <span className="text-xs text-slate-500">{c.industry || '—'}</span>
                      </td>
                      <td className="px-4 py-3 text-left">
                        <span className="text-sm text-slate-700 font-medium">{c.framework_count}</span>
                      </td>
                      <td className="px-4 py-3 text-left">
                        <ProgressBar value={c.avg_completion} className="min-w-[110px]" />
                      </td>
                      <td className="px-4 py-3 text-left">
                        <button
                          onClick={() => navigate(`/partner/companies/${c.id}`)}
                          className="p-1 text-slate-300 hover:text-blue-600 rounded transition-colors"
                        >
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Right column: Recent Activity + Quick Nav */}
        <div className="space-y-6">

          {/* Recent Activity */}
          <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-slate-400" />
                <h2 className="text-sm font-semibold text-slate-900">Recent Activity</h2>
              </div>
              <button
                onClick={() => navigate('/partner/audit-logs')}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
              >
                All logs <ChevronRight className="w-3 h-3" />
              </button>
            </div>
            {recentLogs.length === 0 ? (
              <div className="py-10 text-center">
                <Activity className="w-7 h-7 mx-auto text-slate-200 mb-2" />
                <p className="text-xs text-slate-400">No recent activity</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-50">
                {recentLogs.map(log => (
                  <div key={log.id} className="px-5 py-3 flex items-start gap-3">
                    <span className={`mt-0.5 shrink-0 px-1.5 py-0.5 rounded text-xs font-medium ${actionColor(log.action)}`}>
                      {log.action?.replace(/_/g, ' ').split(' ')[0] ?? 'event'}
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs text-slate-700 truncate">
                        <span className="font-medium">{log.actor_name || 'System'}</span>
                        {' · '}{log.entity_type?.replace(/_/g, ' ')}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">{fmtDateShort(log.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Navigation */}
          <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
              <h2 className="text-sm font-semibold text-slate-900">Quick Access</h2>
            </div>
            <div className="p-3 grid grid-cols-2 gap-2">
              {([
                { icon: Building2,     label: 'Companies',    path: '/partner/companies',                        color: 'text-blue-600 bg-blue-50' },
                { icon: Users,         label: 'Users',        path: '/partner/users',                            color: 'text-violet-600 bg-violet-50' },
                { icon: MessageSquare, label: 'Messages',     path: '/messages',                                 color: 'text-emerald-600 bg-emerald-50' },
                { icon: ClipboardList, label: 'Audit Logs',   path: '/partner/audit-logs',                       color: 'text-amber-600 bg-amber-50' },
                { icon: FileText,      label: 'Frameworks',   path: '/compliance/definition/frameworks',         color: 'text-cyan-600 bg-cyan-50' },
                { icon: BarChart3,     label: 'Templates',    path: '/compliance/definition/templates',          color: 'text-rose-600 bg-rose-50' },
              ] as const).map(({ icon: Icon, label, path, color }) => (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-100 transition-all text-left"
                >
                  <div className={`p-1.5 rounded-lg ${color.split(' ')[1]}`}>
                    <Icon className={`w-3.5 h-3.5 ${color.split(' ')[0]}`} />
                  </div>
                  <span className="text-xs font-medium text-slate-700">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Full Compliance Table ───────────────────────────────────────── */}
      {compliance.length > 0 && (
        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-900">Full Compliance Overview</h2>
            <p className="text-xs text-slate-500 mt-0.5">All framework assignments across the portfolio</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Company</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Standard</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Progress</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden lg:table-cell">Assigned</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {compliance.map((row, i) => (
                  <tr key={i} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3 text-left font-medium text-slate-900">{row.company_name}</td>
                    <td className="px-4 py-3 text-left">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded shrink-0">
                          {row.framework_code || '—'}
                        </span>
                        <span className="text-slate-600 text-xs truncate max-w-[160px]">{row.framework_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-left">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${statusBadge(row.assignment_status)}`}>
                        {row.assignment_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-left">
                      <ProgressBar value={row.completion_percentage} className="min-w-[120px]" />
                    </td>
                    <td className="px-4 py-3 text-left text-xs text-slate-400 hidden lg:table-cell">
                      {row.assigned_at ? fmtDate(row.assigned_at) : '—'}
                    </td>
                    <td className="px-4 py-3 text-left">
                      <button
                        onClick={() => navigate(`/partner/companies/${row.company_id}`)}
                        className="p-1 text-slate-300 hover:text-blue-600 rounded transition-colors"
                        title="View company"
                      >
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
